import {
  AlertIcon,
  ArchiveIcon,
  BeakerIcon,
  BookmarkIcon,
  BriefcaseIcon,
  BugIcon,
  CalendarIcon,
  ClockIcon,
  CodeReviewIcon,
  CodescanIcon,
  CommentDiscussionIcon,
  CommentIcon,
  CopilotIcon,
  DependabotIcon,
  EyeIcon,
  FileDiffIcon,
  FlameIcon,
  GearIcon,
  GitPullRequestIcon,
  GlobeIcon,
  HomeIcon,
  HubotIcon,
  InboxIcon,
  IssueOpenedIcon,
  LinkIcon,
  MentionIcon,
  MeterIcon,
  MoonIcon,
  NorthStarIcon,
  OrganizationIcon,
  PeopleIcon,
  PersonIcon,
  RepoForkedIcon,
  RepoIcon,
  RepoLockedIcon,
  RepoTemplateIcon,
  RocketIcon,
  SmileyIcon,
  SquirrelIcon,
  SunIcon,
  TelescopeIcon,
  TerminalIcon,
  ToolsIcon,
  ZapIcon,
} from '@primer/octicons-react'
import type {ComponentType, ReactNode} from 'react'

/**
 * Host content shown beside a saved view form's controls. As a function, it is called with whether
 * a save is pending, so a control that changes what the save sends can stay off until it settles.
 */
export type SavedViewSaveAwareContent = ReactNode | ((state: {isSaving: boolean}) => ReactNode)

export type SavedViewIssuesRenderer = ComponentType<{
  owner: string
  repo: string
  query: string
  preheatEnabled?: boolean
}>

export interface UserShortcut {
  /**
   * The numeric database id. Used as the path segment for
   * `PUT`/`DELETE /users/shortcuts/:id`, which the server resolves with
   * `SearchShortcut.find_by(id:)`, so it must not be swapped for `globalId`.
   */
  id: string
  /**
   * The GraphQL global id. Canonical identifier for saved-view routes
   * (`/issues/SSC_kgDOAAi2Mw`). The exact shape is a backend detail — dotcom sends
   * `SSC_…`, GHES sends a legacy base64 relay id — so never match it by shape.
   * Optional because older backends omit it; read it through `savedViewRouteId` so
   * numeric ids keep resolving.
   */
  globalId?: string
  name: string
  searchType: 'repositories' | 'issues' | 'pull-requests'
  query: string
  description?: string
  icon: IconName
  color: IconColor
}

export type SavedViewVisibility = 'team' | 'personal'
export type SavedViewScope = 'repository' | 'organization' | 'user'
export type SavedViewId = number | string
export type SavedViewPlacementOwnerType = 'repository' | 'organization' | 'user'
export type ViewVisibility = 'private' | 'shared'

export type ViewSearchType = 'issues' | 'pull-requests'

export interface ViewIndexMetadata {
  placementOwnerType: SavedViewPlacementOwnerType
  searchType: ViewSearchType
}

export interface ViewOpenMetadata {
  viewId: number
  placementOwnerType: SavedViewPlacementOwnerType
  searchType: ViewSearchType
}

export interface ViewNewVisitMetadata {
  placementOwnerType: SavedViewPlacementOwnerType
  searchType: ViewSearchType
  visibilityIsDefault: boolean
}

export interface SavedView {
  id: SavedViewId
  viewId?: number
  name: string
  description: string | null
  icon: IconName
  color: IconColor
  query: string
  position: number | null
  pinned?: boolean
  pinPosition?: number
  visibility: SavedViewVisibility
  createdAt: string
  updatedAt: string
  creatorLogin: string | null
}

export interface SavedViewsResponse {
  savedViews: SavedView[]
  viewerCanManageViews: boolean
  pinningEnabled?: boolean
}

export interface SavedViewPinCaps {
  personal: number
  team: number
}

export type CreateSavedViewSource = 'new' | 'duplicate'

export interface CreateSavedViewVariables<TConfiguration = never> {
  name: string
  query: string
  description?: string
  icon?: IconName
  color?: IconColor
  source?: CreateSavedViewSource
  visibility?: SavedViewVisibility
  // Opaque to saved-views and forwarded as-is; owners (e.g. planning) validate its shape via
  // `ViewConfiguration`.
  configuration?: TConfiguration
}

export interface CreateSavedViewResponse<TConfiguration = never> {
  savedView: SavedView
  configuration?: TConfiguration
}

export interface SetSavedViewPinnedResponse extends CreateSavedViewResponse {
  pinnedViews?: SavedView[]
}

export interface UpdateSavedViewVariables<TConfiguration = never> extends Omit<
  CreateSavedViewVariables<TConfiguration>,
  'source'
> {
  id: SavedViewId
}

export interface UpdateSavedViewResponse<TConfiguration = never> {
  savedView: SavedView
  configuration?: TConfiguration
}

export interface DeleteSavedViewVariables {
  id: SavedViewId
}

export interface DuplicateSavedViewVariables<TConfiguration = never> {
  view: SavedView
  name: string
  // Opaque host-owned configuration persisted alongside the duplicated view when supported.
  configuration?: TConfiguration
}

export interface SetSavedViewPinnedVariables {
  id: SavedViewId
  name: string
  pinned: boolean
  view?: SavedView
}

export interface SidebarLink extends UserShortcut {
  source: 'predefined' | 'user'
  path?: string
}

const defaultIcon: IconName = 'bookmark'
const defaultColor: IconColor = 'gray'

export function getIconColorClassName(color: string | undefined) {
  const colorEntry = colorMap[color?.toLowerCase() as IconColor] || colorMap[defaultColor]
  return colorEntry.foreground
}

export function getBackgroundIconColorClassName(color: string | undefined) {
  const colorEntry = colorMap[color?.toLowerCase() as IconColor] || colorMap[defaultColor]
  return colorEntry.background
}

export function getIconComponent(name: string | undefined) {
  return iconMap[name?.toLowerCase() as IconName] || iconMap[defaultIcon]
}

export function getDisplayName(name: string) {
  return `${name.charAt(0).toUpperCase() + name.slice(1).replaceAll('_', ' ')}`
}

const colorMap = {
  ['gray']: {foreground: 'color-fg-muted', background: 'color-bg-emphasis'},
  ['blue']: {foreground: 'color-fg-accent', background: 'color-bg-accent-emphasis'},
  ['green']: {foreground: 'color-fg-success', background: 'color-bg-success-emphasis'},
  ['orange']: {foreground: 'color-fg-severe', background: 'color-bg-severe-emphasis'},
  ['red']: {foreground: 'color-fg-danger', background: 'color-bg-danger-emphasis'},
  ['pink']: {foreground: 'color-fg-sponsors', background: 'color-bg-sponsors-emphasis'},
  ['purple']: {foreground: 'color-fg-done', background: 'color-bg-done-emphasis'},
} as const

export type IconColor = keyof typeof colorMap

export const supportedIconColors = Object.keys(colorMap) as IconColor[]

const iconMap = {
  ['people']: PeopleIcon,
  ['smiley']: SmileyIcon,
  ['mention']: MentionIcon,
  ['clock']: ClockIcon,
  ['git_pull_request']: GitPullRequestIcon,
  ['zap']: ZapIcon,
  ['issue_opened']: IssueOpenedIcon,
  ['person']: PersonIcon,
  ['comment_discussion']: CommentDiscussionIcon,
  ['organization']: OrganizationIcon,
  ['briefcase']: BriefcaseIcon,
  ['file_diff']: FileDiffIcon,
  ['code_review']: CodeReviewIcon,
  ['codescan']: CodescanIcon,
  ['terminal']: TerminalIcon,
  ['tools']: ToolsIcon,
  ['beaker']: BeakerIcon,
  ['alert']: AlertIcon,
  ['eye']: EyeIcon,
  ['telescope']: TelescopeIcon,
  ['bookmark']: BookmarkIcon,
  ['calendar']: CalendarIcon,
  ['meter']: MeterIcon,
  ['moon']: MoonIcon,
  ['sun']: SunIcon,
  ['flame']: FlameIcon,
  ['bug']: BugIcon,
  ['north_star']: NorthStarIcon,
  ['rocket']: RocketIcon,
  ['squirrel']: SquirrelIcon,
  ['hubot']: HubotIcon,
  ['dependabot']: DependabotIcon,
  ['repo']: RepoIcon,
  ['repo_locked']: RepoLockedIcon,
  ['link']: LinkIcon,
  ['repo_forked']: RepoForkedIcon,
  ['repo_template']: RepoTemplateIcon,
  ['archive']: ArchiveIcon,
  ['home']: HomeIcon,
  ['inbox']: InboxIcon,
  ['gear']: GearIcon,
  ['copilot']: CopilotIcon,
  ['globe']: GlobeIcon,
  ['comment']: CommentIcon,
} as const

export type IconName = keyof typeof iconMap

export const supportedIconNames = Object.keys(iconMap) as IconName[]
